import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { useDomain, DOMAINS } from '../../../../contexts/DomainContext';
import { useTheme } from '@mui/material';
import { useGraphQL } from '../../../../apollo/useGraphQL';
import useLocalStorageVO from '../../../Common/UseLocalStorageVO';
import Message from '../../../../utils/message/Message';
import { gql } from '@apollo/client';
import { getGridCodeList } from '../../../../api/standardInfo/commonCodeApi';
import Swal from 'sweetalert2';
import { ALERT_MESSAGES } from '../constants';

// 제품 정보 조회를 위한 GraphQL 쿼리
const PRODUCTS_QUERY = gql`
    query getProductMaterials {
        productMaterials {
            systemMaterialId
            userMaterialId
            materialName
            materialStandard
            materialCategory
            materialType
            unit
        }
    }
`;

// KPI 설비 정보 조회를 위한 GraphQL 쿼리
const KPI_EQUIPMENTS_QUERY = gql`
    query getKPIEquipments($filter: EquipmentFilter) {
        getKPIEquipments(filter: $filter) {
            equipmentId
            equipmentName
            equipmentType
            factoryId
            factoryName
            lineId
            lineName
            equipmentStatus
        }
    }
`;

// 창고 정보 조회를 위한 GraphQL 쿼리
const WAREHOUSE_QUERY = gql`
    query getWarehouse($filter: WarehouseFilter) {
        getWarehouse(filter: $filter) {
            warehouseId
            warehouseName
            factoryId
            factoryName
            warehouseType
        }
    }
`;

// 고객사(Vendor) 정보 조회를 위한 GraphQL 쿼리
const VENDOR_QUERY = gql`
    query getVendors($filter: VendorFilter) {
        getVendors(filter: $filter) {
            vendorId
            vendorName
            vendorType
            businessRegNo
            ceoName
            businessType
            address
            telNo
        }
    }
`;

// KPI 구독 정보 조회를 위한 GraphQL 쿼리
const KPI_SUBSCRIPTIONS_QUERY = gql`
    query getKpiSubscriptions {
        getKpiSubscriptions {
            site
            compCd
            kpiIndicatorCd
            categoryCd
            currentValue
            targetValue
            description
            transmitCycle
            sort
            flagActive
        }
    }
`;

// 생산실적 저장을 위한 GraphQL 뮤테이션
const SAVE_PRODUCTION_RESULT_MUTATION = gql`
    mutation saveKpiProductionResult($createdRows: [ProductionResultInput]) {
        saveKpiProductionResult(createdRows: $createdRows)
    }
`;

// 제품 정보에 표시값 추가 유틸리티 함수
const enrichProductWithDisplayValues = (product) => {
  return {
    ...product,
    value: product.systemMaterialId,
    label: `${product.userMaterialId} (${product.materialName || ''})`
  };
};

/**
 * KPI 생산실적등록 기능 제공 커스텀 훅
 *
 * @returns {Object} KPI 생산실적등록에 필요한 상태 및 함수들
 */
export const useKPIProductionResult = () => {
  // 테마, 도메인 및 시스템 설정
  const theme = useTheme();
  const { domain } = useDomain();
  const isDarkMode = theme.palette.mode === 'dark';
  const { loginUser } = useLocalStorageVO();

  // 상태 관리
  const [isLoading, setIsLoading] = useState(true);
  const [kpiEquipments, setKpiEquipments] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // 옵션 목록 상태
  const [productOptions, setProductOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [kpiSubscriptions, setKpiSubscriptions] = useState([]);

  // 로딩 상태 추가
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [isWarehousesLoading, setIsWarehousesLoading] = useState(false);
  const [isVendorsLoading, setIsVendorsLoading] = useState(false);
  const [isKpiSubscriptionsLoading, setIsKpiSubscriptionsLoading] = useState(false);

  // GraphQL 기능 사용
  const { executeQuery, executeMutation } = useGraphQL();

  // 제품 정보 로드 함수
  const loadProductMaterials = useCallback(async () => {
    if (productOptions.length > 0 || isProductsLoading) {
      return productOptions;
    }

    setIsProductsLoading(true);

    try {
      const response = await executeQuery({ query: PRODUCTS_QUERY });

      if (response?.data?.productMaterials) {
        const enrichedProducts = response.data.productMaterials.map(
          product => enrichProductWithDisplayValues(product)
        );

        setProductOptions(enrichedProducts);
        return enrichedProducts;
      } else {
        console.error("제품 정보가 없거나 형식이 맞지 않습니다.");
        return [];
      }
    } catch (error) {
      console.error("제품 정보 로드 오류:", error);
      return [];
    } finally {
      setIsProductsLoading(false);
    }
  }, [executeQuery, isProductsLoading, productOptions.length]);

  // KPI 설비 정보 로드 함수
  const loadKPIEquipments = useCallback(async (filter = {}) => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await executeQuery({
        query: KPI_EQUIPMENTS_QUERY,
        variables: { filter }
      });

      if (response?.data?.getKPIEquipments) {
        const equipments = response.data.getKPIEquipments;
        setKpiEquipments(equipments);
        return equipments;
      } else {
        setKpiEquipments([]);
        return [];
      }
    } catch (error) {
      console.error("KPI 설비 정보 로드 오류:", error);
      setErrorMessage(ALERT_MESSAGES.LOAD_ERROR);
      setKpiEquipments([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [executeQuery]);

  // 창고 정보 로드 함수
  const loadWarehouses = useCallback(async () => {
    if (warehouseOptions.length > 0 || isWarehousesLoading) {
      return warehouseOptions;
    }

    setIsWarehousesLoading(true);

    try {
      const response = await executeQuery({
        query: WAREHOUSE_QUERY,
        variables: {
          filter: {
            factoryId: "",
            factoryName: "",
            warehouseId: "",
            warehouseName: "",
            warehouseType: "PRODUCT_WAREHOUSE"
          }
        }
      });

      if (response?.data?.getWarehouse) {
        const formattedWarehouses = response.data.getWarehouse.map(warehouse => ({
          value: warehouse.warehouseId,
          label: warehouse.warehouseName || warehouse.warehouseId,
          factoryName: warehouse.factoryName,
          warehouseType: warehouse.warehouseType
        }));

        setWarehouseOptions(formattedWarehouses);
        return formattedWarehouses;
      }
      return [];
    } catch (error) {
      console.error("창고 정보 로드 오류:", error);
      return [];
    } finally {
      setIsWarehousesLoading(false);
    }
  }, [executeQuery, isWarehousesLoading, warehouseOptions.length]);

  // 단위 정보 로드 함수
  const loadUnitOptions = useCallback(async () => {
    if (unitOptions.length > 0) {
      return unitOptions;
    }

    try {
      const codes = await getGridCodeList(['UNIT']);
      const units = codes['UNIT'] || [];
      setUnitOptions(units);
      return units;
    } catch (error) {
      console.error("단위 정보 로드 오류:", error);
      return [];
    }
  }, [unitOptions.length]);

  // 고객사(Vendor) 정보 로드 함수
  const loadVendors = useCallback(async () => {
    if (vendorOptions.length > 0 || isVendorsLoading) {
      return vendorOptions;
    }

    setIsVendorsLoading(true);

    try {
      const response = await executeQuery({
        query: VENDOR_QUERY,
        variables: {
          filter: {
            vendorId: "",
            vendorName: "",
            ceoName: "",
            businessRegNo: "",
            businessType: ""
          }
        }
      });

      if (response?.data?.getVendors) {
        const formattedVendors = response.data.getVendors.map(vendor => ({
          value: vendor.vendorId,
          label: vendor.vendorName || vendor.vendorId,
          vendorType: vendor.vendorType
        }));

        setVendorOptions(formattedVendors);
        return formattedVendors;
      }
      return [];
    } catch (error) {
      console.error("고객사 정보 로드 오류:", error);
      return [];
    } finally {
      setIsVendorsLoading(false);
    }
  }, [executeQuery, isVendorsLoading, vendorOptions.length]);

  // KPI 구독 정보 로드 함수
  const loadKpiSubscriptions = useCallback(async () => {
    if (kpiSubscriptions.length > 0 || isKpiSubscriptionsLoading) {
      return kpiSubscriptions;
    }

    setIsKpiSubscriptionsLoading(true);

    try {
      const response = await executeQuery({
        query: KPI_SUBSCRIPTIONS_QUERY
      });

      if (response?.data?.getKpiSubscriptions) {
        const subscriptions = response.data.getKpiSubscriptions.filter(
          sub => sub.flagActive && sub.targetValue && sub.targetValue > 0
        );

        setKpiSubscriptions(subscriptions);
        return subscriptions;
      }
      return [];
    } catch (error) {
      console.error("KPI 구독 정보 로드 오류:", error);
      return [];
    } finally {
      setIsKpiSubscriptionsLoading(false);
    }
  }, [executeQuery, isKpiSubscriptionsLoading, kpiSubscriptions.length]);

  // 검색 실행 함수
  const handleSearch = useCallback((data) => {
    const filter = {
      factoryId: data.factoryId || "",
      factoryName: data.factoryId || "",
      lineId: data.lineId || "",
      lineName: data.lineId || "",
      equipmentId: data.equipmentId || "",
      equipmentName: data.equipmentName || "",
      equipmentSn: "",
      equipmentType: ""
    };

    // KPI 설비 검색 실행
    loadKPIEquipments(filter);
  }, [loadKPIEquipments]);

  // 설비별 생산실적 저장 함수
  const handleSaveEquipmentResult = useCallback(async (equipmentId, productionData) => {
    try {
      // DateTime을 한국 시간 ISO 문자열로 변환 (KST+09:00)
      const formatDateTimeForServer = (date) => {
        if (!date) return null;
        if (date instanceof Date) {
          // 로컬 시간을 그대로 사용하여 KST ISO 문자열 생성
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
        }
        return date;
      };

      // Date만 YYYY-MM-DD 형식으로 변환
      const formatDateForServer = (date) => {
        if (!date) return null;
        if (date instanceof Date) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        return date;
      };

      // 저장할 데이터 구성 (KPI 독립생산실적 저장 로직)
      const saveData = {
        workOrderId: null, // 독립 생산실적이므로 null
        productId: productionData.productId,
        equipmentId: productionData.equipmentId,
        warehouseId: productionData.warehouseId,
        goodQty: Number(productionData.goodQty) || 0,
        defectQty: Number(productionData.defectQty) || 0,
        resultInfo: productionData.resultInfo || "",
        defectCause: productionData.defectCause || "",
        prodStartTime: formatDateTimeForServer(productionData.prodStartTime),
        prodEndTime: formatDateTimeForServer(productionData.prodEndTime),
        flagActive: true,
        // KPI 독립 생산실적을 위한 추가 필드
        customerId: productionData.customerId || "",
        orderDate: formatDateForServer(productionData.orderDate),
        deliveryDate: formatDateForServer(productionData.deliveryDate),
        unitPrice: Number(productionData.unitPrice) || 0,
        autoShipment: productionData.autoShipment !== undefined ? productionData.autoShipment : true
      };

      // 불량정보가 있으면 백엔드 형식으로 변환하여 포함
      if (productionData.defectInfos && productionData.defectInfos.length > 0) {
        saveData.defectInfos = productionData.defectInfos.map(info => ({
          workOrderId: null, // 독립 생산실적이므로 null
          productId: productionData.productId,
          defectQty: Number(info.defectQty) || 0,
          defectType: info.defectType,
          defectCause: info.defectDesc || '',
          resultInfo: info.defectType,
          state: 'NEW',
          flagActive: true
        }));
      }

      // GraphQL 뮤테이션 실행
      const response = await executeMutation({
        mutation: SAVE_PRODUCTION_RESULT_MUTATION,
        variables: {
          createdRows: [saveData]
        }
      });

      if (response?.data?.saveKpiProductionResult) {
        Swal.fire({
          icon: 'success',
          title: '저장 완료',
          text: ALERT_MESSAGES.SAVE_SUCCESS,
          confirmButtonText: '확인'
        });

        // 저장 성공 후 해당 설비의 폼을 초기화하거나 새로고침
        setRefreshKey(prev => prev + 1);
        
        // 성공 시 정상 반환 (에러 없음)
        return;
      } else {
        throw new Error('저장 응답이 올바르지 않습니다.');
      }
    } catch (error) {
      console.error('생산실적 저장 오류:', error);
      Swal.fire({
        icon: 'error',
        title: '저장 실패',
        text: ALERT_MESSAGES.SAVE_ERROR,
        confirmButtonText: '확인'
      });
      
      // 실패 시 에러를 다시 던져서 컴포넌트에서 catch할 수 있도록 함
      throw error;
    }
  }, [executeMutation]);

  // 도메인별 색상 설정 함수
  const getTextColor = useCallback(() => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#f0e6d9' : 'rgba(0, 0, 0, 0.87)';
    }
    return isDarkMode ? '#b3c5e6' : 'rgba(0, 0, 0, 0.87)';
  }, [domain, isDarkMode]);

  const getBgColor = useCallback(() => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? 'rgba(45, 30, 15, 0.5)' : 'rgba(252, 235, 212, 0.6)';
    }
    return isDarkMode ? 'rgba(0, 27, 63, 0.5)' : 'rgba(232, 244, 253, 0.6)';
  }, [domain, isDarkMode]);

  const getBorderColor = useCallback(() => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#3d2814' : '#f5e8d7';
    }
    return isDarkMode ? '#1e3a5f' : '#e0e0e0';
  }, [domain, isDarkMode]);

  // 컴포넌트 마운트 시 초기 데이터 로드
  useEffect(() => {
    // 옵션 데이터 로드
    loadProductMaterials();
    loadWarehouses();
    loadUnitOptions();
    loadVendors();
    loadKpiSubscriptions(); // KPI 구독 정보 로드 추가

    // 기본적으로 모든 KPI 설비 로드
    const defaultFilter = {
      factoryId: "",
      factoryName: "",
      lineId: "",
      lineName: "",
      equipmentId: "",
      equipmentName: "",
      equipmentSn: "",
      equipmentType: ""
    };
    loadKPIEquipments(defaultFilter);
  }, []);

  return {
    // KPI 설비 관련
    isLoading,
    kpiEquipments,
    errorMessage,
    handleSearch,
    handleSaveEquipmentResult,

    // 색상 및 테마
    getTextColor,
    getBgColor,
    getBorderColor,

    // 옵션 데이터
    productOptions,
    warehouseOptions,
    unitOptions,
    vendorOptions,
    kpiSubscriptions, // KPI 구독 정보 추가

    // 리프레시 키
    refreshKey
  };
};